import { useState, useEffect} from 'react';
import { Form, Row, Col, Button, Card, Image } from 'react-bootstrap';
import { validateURL} from './utils/validation';
import worldLanguages from './data/worldLanguages'
import continents from './data/continents'
import countries from './data/countries'
import countryContinent from './data/countryContinent'
import Select from 'react-select';
import { useParams, Link } from 'react-router-dom';
import icValidationError from './assets/img/ic-danger.svg';
import icCity from './assets/img/ic-city.svg';
import icGlobe from './assets/img/ic-globe.svg';
import icNumber from './assets/img/ic-number.svg';
import icPeople from './assets/img/ic-user-group.svg';
import icNotes from './assets/img/ic-news.svg';
import icLink from './assets/img/ic-link-square.svg';
import icFlag from './assets/img/ic-banner.svg';

const CityAddGeneral = ({
    formData,
    handleChange,
    errors,
    geocodeLoading,
    geocodeError,
    onRefreshCoordinates,
    handleGeneralSubmit,
    isEditPage,
    isReviewPage
}) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({}); // ✅ Renamed errors state to avoid conflict

    const [urlInput, setUrlInput] = useState(formData.url || '');

    const { id } = useParams();
    
    useEffect(() => {
        setUrlInput(formData.url || ''); // ✅ Ensure urlInput updates when formData changes
    }, [formData.url]);

    const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));

    // ✅ normalize + validate
    const normalizeUrl = (raw) => {
        const s = (raw || "").trim();
        if (!s) return null;

        // If no scheme, default to https://
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;

        try {
            const u = new URL(withScheme);
            // Only allow http/https
            if (!["http:", "https:"].includes(u.protocol)) return null;
            return u.toString();
        } catch {
            return null;
        }
    };

    const safeHref = normalizeUrl(urlInput);

    // Handle URL field change
    const handleURLChange = (e) => {
        const { value } = e.target;
        setUrlInput(value);
    
        clearTimeout(window.urlValidationTimeout);
        window.urlValidationTimeout = setTimeout(() => {
            if (value && !validateURL(value)) {
                setLocalErrors((prevErrors) => ({ ...prevErrors, url: 'Please enter a valid URL.' }));
            } else {
                setLocalErrors((prevErrors) => ({ ...prevErrors, url: '' }));
                handleChange({ target: { name: 'url', value } });  // Make sure this updates the main formData
            }
        }, 800);
    };
    
    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        if (typeof handleGeneralSubmit === 'function') {
            const ok = await handleGeneralSubmit(e); // ✅ boolean from submit
            if (!ok) return; // ✅ do not exit edit mode
            setIsEditing(false); // ✅ only on success
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } }); // Restore previous values
    };

    const languageOptions = worldLanguages.map(language => ({ value: language, label: language }));

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">1. </span>General Info</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <Row>
                            {/* Form Fields */}
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={2}>
                                        <Form.Group className="validated" controlId="names">
                                            <Form.Label>City Name <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                required
                                                disabled={!isEditing}
                                            />
                                            {!!errors.name && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.name} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.name}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="validated" controlId="country">
                                            <Form.Label>Country <span>*</span></Form.Label>
                                            <Form.Select
                                                name="country"
                                                value={formData.country?.id ?? ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const opt = (countryOptions || []).find(o => String(o.value) === String(val));
                                                    handleChange({
                                                        target: {
                                                            name: 'country',
                                                            value: opt ? { id: opt.value, name: opt.label } : {}
                                                        }
                                                    });
                                                    const detectedContinent = val ? (countryContinent[val.toUpperCase()] || '') : '';
                                                    if (detectedContinent) {
                                                        handleChange({ target: { name: 'continent', value: detectedContinent } });
                                                    }
                                                }}
                                                disabled={!isEditing}
                                                isInvalid={!!errors.country}
                                                required
                                            >
                                                <option value="">Select</option>
                                                {Array.isArray(countryOptions) &&
                                                countryOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </Form.Select>
                                            {!!errors.country && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.country} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.country}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="validated" controlId="continent">
                                            <Form.Label>Continent <span>*</span></Form.Label>
                                            <Form.Select
                                                name="continent"
                                                value={formData.continent || ''}
                                                onChange={() => {}}
                                                disabled
                                                isInvalid={!!errors.continent}
                                                required
                                            >
                                                <option value="">Select</option>
                                                {Array.isArray(continents) &&
                                                continents.map((opt) => (
                                                    <option key={opt.value ?? opt} value={opt.value ?? opt}>{opt.label ?? opt}</option>
                                                ))}
                                            </Form.Select>
                                            {!!errors.continent && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img src={icValidationError} alt={errors.continent} className="custom-error-icon" />
                                                    <div className="custom-tooltip">{errors.continent}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="validated" controlId="citizensNumber">
                                            <Form.Label>City citizen number <span>*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="citizensNumber"
                                                value={formData.citizensNumber}
                                                onChange={handleChange}
                                                isInvalid={!!errors.citizensNumber}
                                                required
                                                disabled={!isEditing}
                                            />
                                            {/* ✅ Custom error icon + tooltip */}
                                            {!!errors.citizensNumber && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.citizensNumber}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">{errors.citizensNumber}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="validated" controlId="regionCitizensNumber">
                                            <Form.Label>Region citizen number <span>*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="regionCitizensNumber"
                                                value={formData.regionCitizensNumber}
                                                onChange={handleChange}
                                                isInvalid={!!errors.regionCitizensNumber}
                                                required
                                                disabled={!isEditing}
                                            />
                                            {/* ✅ Custom error icon + tooltip */}
                                            {!!errors.regionCitizensNumber && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.regionCitizensNumber}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">{errors.regionCitizensNumber}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={3}>
                                        <Form.Group className="validated" controlId="latitude">
                                            <Form.Label>Latitude <span>*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="any"
                                                name="latitude"
                                                value={formData.latitude ?? ''}
                                                onChange={handleChange}
                                                isInvalid={!!errors.latitude}
                                                disabled={!isEditing}
                                            />
                                            {!!errors.latitude && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.latitude}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">{errors.latitude}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group className="validated" controlId="longitude">
                                            <Form.Label>Longitude <span>*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="any"
                                                name="longitude"
                                                value={formData.longitude ?? ''}
                                                onChange={handleChange}
                                                isInvalid={!!errors.longitude}
                                                disabled={!isEditing}
                                            />
                                            {!!errors.longitude && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.longitude}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">{errors.longitude}</div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group controlId="coordinatesStatus">
                                            <Form.Label>Coordinates Status</Form.Label>
                                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                                <p className="mb-0">
                                                    <strong>
                                                        {geocodeLoading
                                                            ? 'Detecting coordinates...'
                                                            : geocodeError
                                                                ? geocodeError
                                                                : formData.latitude && formData.longitude
                                                                    ? 'Coordinates detected automatically.'
                                                                    : 'Enter city and country to detect coordinates.'}
                                                    </strong>
                                                </p>

                                                {isEditing && (
                                                    <Button
                                                        type="button"
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={onRefreshCoordinates}
                                                        disabled={geocodeLoading || !formData.name || !formData.country?.name}
                                                    >
                                                        Refresh Coordinates
                                                    </Button>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="cityId">
                                                    <Form.Label>City ID <span>*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="cityId"
                                                        value={formData.cityId}
                                                        onChange={handleChange}
                                                        isInvalid={!!errors.cityId}
                                                        required
                                                        disabled={!isEditing}
                                                    />
                                                    {/* ✅ Custom error icon + tooltip */}
                                                    {!!errors.cityId && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={errors.cityId}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">{errors.cityId}</div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="url">
                                                    <Form.Label>Regional URL</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="url"
                                                        value={urlInput}
                                                        onChange={handleURLChange}
                                                        isInvalid={!!errors.url || !!localErrors.url}
                                                        disabled={!isEditing}
                                                    />
                                                    {(!!errors.url || !!localErrors.url) && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={(errors.url || localErrors.url)}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">{errors.url || localErrors.url}</div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="validated" controlId="languagesSpoken">
                                                    <Form.Label>Local Language</Form.Label>
                                                    <Select
                                                        options={Array.isArray(languageOptions) ? languageOptions : []}
                                                        value={
                                                        (Array.isArray(formData.languagesSpoken) ? formData.languagesSpoken : [])
                                                            .map(val => (languageOptions || []).find(opt => String(opt.value) === String(val)))
                                                            .filter(Boolean)
                                                        }
                                                        onChange={(selected) =>
                                                        isEditing &&
                                                        handleChange({
                                                                target: {
                                                                    name: 'languagesSpoken',
                                                                    value: Array.isArray(selected) ? selected.map(s => s.value) : []
                                                                }
                                                            })
                                                        }
                                                        isMulti
                                                        isClearable
                                                        isDisabled={!isEditing}            // ✅ Disable when not editing
                                                        className="react-select"
                                                        classNamePrefix="react-select"
                                                        placeholder="Select…"
                                                    />

                                                    {/* ✅ Custom error icon + tooltip (optional) */}
                                                    {!!errors?.languagesSpoken && (
                                                        <div className="custom-error-icon-wrapper">
                                                        <img
                                                            src={icValidationError}
                                                            alt={errors.languagesSpoken}
                                                            className="custom-error-icon"
                                                        />
                                                        <div className="custom-tooltip">{errors.languagesSpoken}</div>
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group controlId="universitiesNumber">
                                                    <Form.Label>Number of universities</Form.Label>
                                                    <p><strong>{formData.universitiesNumber}</strong></p>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group controlId="studentsNumber">
                                                    <Form.Label>Number of students</Form.Label>
                                                    <p><strong>{formData.studentsNumber}</strong></p>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col md={6}>
                                        <Row>
                                            <Col md={12}>
                                                <Form.Group controlId="cityRegionDescription">
                                                    <Form.Label>City Region Description</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        name="cityRegionDescription"
                                                        value={formData.cityRegionDescription}
                                                        onChange={handleChange}
                                                        isInvalid={!!errors.cityRegionDescription}
                                                        disabled={!isEditing}
                                                        style={{ height: '11.9rem' }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    {isEditing && id && (
                                        <Link to={`/city-edit-universities/${id}`} className="font-size-md">
                                            <strong>Add/Edit Universities</strong>
                                        </Link>
                                    )}
                                </Row>
                                {isReviewPage && (
                                    <Row>
                                        <Col md={6}>
                                            <p>Under Review</p> {/* ✅ Show only in Review Page */}
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row className="mb-4">
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icCity} alt="City Name" />
                                        </span>
                                        <span className="text">
                                            City Name
                                            <br />
                                            <strong>
                                                {formData.name || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icGlobe} alt="Country" />
                                        </span>
                                        <span className="text">
                                            Country
                                            <br />
                                            <strong>
                                                {formData.country.name || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icNumber} alt="City citizen number" />
                                        </span>
                                        <span className="text">
                                            City citizen number
                                            <br />
                                            <strong>
                                                {formData.citizensNumber || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icNumber} alt="Region citizen number" />
                                        </span>
                                        <span className="text">
                                            Region citizen number
                                            <br />
                                            <strong>
                                                {formData.regionCitizensNumber || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icLink} alt="Regional URL" />
                                        </span>
                                        <span className="text">
                                            Regional URL
                                            <br />
                                            <strong>
                                                {safeHref ? (
                                                    <a href={safeHref} target="_blank" rel="noopener noreferrer">
                                                        {formData.url}
                                                    </a>
                                                ) : (
                                                    "N/A"
                                                )}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icFlag} alt="Local language(s)" />
                                        </span>
                                        <span className="text">
                                            Local language(s)
                                            <br />
                                            <strong>
                                                {Array.isArray(formData.languagesSpoken) && formData.languagesSpoken.length
                                                    ? formData.languagesSpoken.join(', ')
                                                    : 'N/A'
                                                }
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icCity} alt="Number of Universities" />
                                        </span>
                                        <span className="text">
                                            Number of Universities
                                            <br />
                                            <strong>
                                                {formData.universitiesNumber || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icPeople} alt="Number of Students " />
                                        </span>
                                        <span className="text">
                                            Number of Students 
                                            <br />
                                            <strong>
                                                {formData.studentsNumber || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icNotes} alt="City Region Description" />
                                        </span>
                                        <span className="text">
                                            City Region Description
                                            <br />
                                            <strong>
                                                {formData.cityRegionDescription || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>    
        </>
    );
};

export default CityAddGeneral;
